import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaChevronLeft, FaChevronRight, FaImage, FaTimes, FaUpload } from "react-icons/fa";
import "./OrderPicturesField.css";

const OrderPicturesField = ({
  pictures = [],
  files = [],
  deletedPictureIds = [],
  error,
  disabled = false,
  onAddFiles,
  onRemoveExisting,
  onRemoveFile,
  groupClassName = "input-group full-width",
  showError = true,
}) => {
  const inputRef = useRef(null);
  const [filePreviews, setFilePreviews] = useState([]);
  const [activePictureIndex, setActivePictureIndex] = useState(null);
  const deletedIdSet = useMemo(
    () => new Set(deletedPictureIds.map((id) => String(id))),
    [deletedPictureIds]
  );

  const visiblePictures = pictures.filter((picture) => (
    picture?.id !== undefined && !deletedIdSet.has(String(picture.id))
  ));

  useEffect(() => {
    if (!files.length) {
      setFilePreviews([]);
      return undefined;
    }

    const previews = files.map((file) => ({
      file,
      url: URL.createObjectURL(file),
    }));
    setFilePreviews(previews);

    return () => {
      previews.forEach((preview) => URL.revokeObjectURL(preview.url));
    };
  }, [files]);

  const displayPictures = [
    ...visiblePictures.map((picture) => ({
      id: picture.id,
      url: picture.url,
      alt: "صورة الطلب",
      type: "saved",
    })),
    ...filePreviews.map((preview, index) => ({
      id: `new-${preview.file.name}-${index}`,
      url: preview.url,
      alt: "صورة جديدة",
      type: "new",
      fileIndex: index,
    })),
  ];
  const hasPictures = visiblePictures.length > 0 || filePreviews.length > 0;
  const activePicture = activePictureIndex === null ? null : displayPictures[activePictureIndex];

  useEffect(() => {
    if (activePictureIndex !== null && activePictureIndex >= displayPictures.length) {
      setActivePictureIndex(null);
    }
  }, [activePictureIndex, displayPictures.length]);

  useEffect(() => {
    if (activePictureIndex === null) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setActivePictureIndex(null);
      }

      if (event.key === "ArrowLeft") {
        setActivePictureIndex((prevIndex) => (
          prevIndex === null ? prevIndex : (prevIndex + 1) % displayPictures.length
        ));
      }

      if (event.key === "ArrowRight") {
        setActivePictureIndex((prevIndex) => (
          prevIndex === null
            ? prevIndex
            : (prevIndex - 1 + displayPictures.length) % displayPictures.length
        ));
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activePictureIndex, displayPictures.length]);

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length && onAddFiles) {
      onAddFiles(selectedFiles);
    }

    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const showPreviousPicture = () => {
    setActivePictureIndex((prevIndex) => (
      prevIndex === null
        ? prevIndex
        : (prevIndex - 1 + displayPictures.length) % displayPictures.length
    ));
  };

  const showNextPicture = () => {
    setActivePictureIndex((prevIndex) => (
      prevIndex === null ? prevIndex : (prevIndex + 1) % displayPictures.length
    ));
  };

  return (
    <div className={`${groupClassName} order-picture-field`}>
      <label>صور الطلب</label>

      <div className={`pictures-grid ${hasPictures ? "" : "empty"}`}>
        {hasPictures ? (
          <>
            {displayPictures.map((picture, index) => (
              <div className="picture-item" key={picture.id}>
                <button
                  type="button"
                  className="picture-view-btn"
                  onClick={() => setActivePictureIndex(index)}
                  aria-label="عرض الصورة بحجم أكبر"
                >
                  <img src={picture.url} alt={picture.alt} />
                </button>
                {!disabled && (
                  <button
                    type="button"
                    className="picture-remove-btn"
                    onClick={() => {
                      if (picture.type === "saved") {
                        onRemoveExisting(picture.id);
                      } else {
                        onRemoveFile(picture.fileIndex);
                      }
                    }}
                    aria-label="إزالة الصورة"
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
            ))}
          </>
        ) : (
          <div className="picture-placeholder">
            <FaImage />
            <span>لا توجد صور</span>
          </div>
        )}
      </div>

      {!disabled && (
        <div className="picture-actions">
          <label className="picture-select-btn">
            <FaUpload />
            <span>إضافة صور</span>
            <input
              ref={inputRef}
              type="file"
              name="pictures"
              accept="image/jpeg,image/png,image/webp"
              multiple
              onChange={handleFileChange}
            />
          </label>
        </div>
      )}

      {showError && error && <small className="error-text">{error}</small>}

      {activePicture && (
        <div
          className="picture-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label="عرض صورة الطلب"
          onClick={() => setActivePictureIndex(null)}
        >
          <button
            type="button"
            className="lightbox-close"
            onClick={() => setActivePictureIndex(null)}
            aria-label="إغلاق"
          >
            <FaTimes />
          </button>

          {displayPictures.length > 1 && (
            <>
              <button
                type="button"
                className="lightbox-nav lightbox-prev"
                onClick={(event) => {
                  event.stopPropagation();
                  showPreviousPicture();
                }}
                aria-label="الصورة السابقة"
              >
                <FaChevronRight />
              </button>

              <button
                type="button"
                className="lightbox-nav lightbox-next"
                onClick={(event) => {
                  event.stopPropagation();
                  showNextPicture();
                }}
                aria-label="الصورة التالية"
              >
                <FaChevronLeft />
              </button>
            </>
          )}

          <img
            className="lightbox-image"
            src={activePicture.url}
            alt={activePicture.alt}
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default OrderPicturesField;
